import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';
import move from 'ext/lib/move';
import { userPropertiesShape } from 'ext/entities/user-properties';
import { BlockContentShape } from 'entities/block/shape';
import { actionsMapper } from 'components/SideBarSettings/Shared/Actions/helpers';
import ConditionalCheckPanel from './ConditionalCheckPanel';
import { ChecksContainer } from './ConditionalAction.styled';

function ConditionalAction({
  conditionalAction,
  contextConfig,
  hasSurveyBlock,
  userProperties,
  allSurveyBlocks,
  isExperienceFirstStep,
  onActionsChange,
}) {
  const [order, setOrder] = useState(conditionalAction?.config.checks || []);

  useEffect(() => {
    setOrder(conditionalAction?.config.checks || []);
  }, [conditionalAction?.config.checks]);

  const onConditionalActionsChange = checkId => delta => {
    const updatedConditionalAction = {
      ...conditionalAction,
      config: {
        ...conditionalAction.config,
        checks: conditionalAction.config.checks.map(check => {
          if (check.id !== checkId) {
            return check;
          }

          if (delta.condition) {
            return {
              ...check,
              ...delta,
            };
          }

          return {
            ...check,
            actions: actionsMapper({
              prevActions: check.actions,
              newActions: delta,
              hasSurveyBlock,
            }),
          };
        }),
      },
    };

    onActionsChange({ actions: [updatedConditionalAction] });
  };

  const handleDelete = blockId => {
    const updatedConditionalAction = {
      ...conditionalAction,
      config: {
        ...conditionalAction.config,
        checks: conditionalAction.config.checks.filter(
          check => check.id !== blockId
        ),
      },
    };

    onActionsChange({ actions: [updatedConditionalAction] });
  };

  const handleAddCondition = () => {
    const updatedConditionalAction = {
      ...conditionalAction,
      config: {
        ...conditionalAction.config,
        checks: [
          ...conditionalAction.config.checks.filter(
            check => check.condition !== null
          ),
          { id: uuid(), condition: {}, actions: [] },
          ...conditionalAction.config.checks.filter(
            check => check.condition === null
          ),
        ],
      },
    };

    onActionsChange({ actions: [updatedConditionalAction] });
  };

  const handleDrag = useCallback(
    (from, to) => {
      setOrder(current => move(current, from, to));
    },
    [setOrder]
  );

  const handleDrop = useCallback(
    (item, commit) => {
      if (commit) {
        const { index: from, target: to } = item;
        const updatedChecks = move(conditionalAction.config.checks, from, to);
        const updatedConditionalAction = {
          ...conditionalAction,
          config: {
            ...conditionalAction.config,
            checks: updatedChecks,
          },
        };
        onActionsChange({ actions: [updatedConditionalAction] });
      } else {
        setOrder(conditionalAction.config.checks);
      }
    },
    [conditionalAction, onActionsChange, setOrder]
  );

  return (
    <ChecksContainer>
      {order.map((check, index) => (
        <ConditionalCheckPanel
          key={check.id}
          check={check}
          index={index}
          allSurveyBlocks={allSurveyBlocks}
          userProperties={userProperties}
          contextConfig={contextConfig}
          hasSurveyBlock={hasSurveyBlock}
          isExperienceFirstStep={isExperienceFirstStep}
          onConditionalActionsChange={onConditionalActionsChange}
          onDelete={handleDelete}
          onAddCondition={handleAddCondition}
          onDrag={handleDrag}
          onDrop={handleDrop}
        />
      ))}
    </ChecksContainer>
  );
}

ConditionalAction.propTypes = {
  conditionalAction: PropTypes.shape({
    config: PropTypes.shape({
      checks: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  }),
  contextConfig: PropTypes.shape({
    label: PropTypes.string,
    description: PropTypes.string,
  }),
  hasSurveyBlock: PropTypes.bool,
  userProperties: userPropertiesShape,
  allSurveyBlocks: PropTypes.arrayOf(BlockContentShape),
  isExperienceFirstStep: PropTypes.bool,
  onActionsChange: PropTypes.func,
};

export default ConditionalAction;
